/**
 * @file menu-registry.ts
 * @description 菜單註冊表 + 能力過濾純函數（Task 8 動態 RBAC Phase 2）
 *
 * 設計原則：
 * 1. `MENU_TREES`：每種工作台類型（WorkbenchType）對應一棵基準菜單樹（MenuItemDef[]）。
 *    - admin / manager 共用同一棵 admin 型基準樹，差異由能力過濾（filterMenuTree）產生。
 *    - decider / auditor / accountant 各有獨立樹，照原硬編版本結構定義。
 * 2. `filterMenuTree`：遞歸過濾函數。
 *    - 無 requiredCapability → 恆亮（所有用戶可見）。
 *    - 有 requiredCapability → 調用 `userCan(user, cap)` 判斷。
 *    - 命中 hiddenKeys → 強制隱藏（支援分組 key 與葉 key）。
 *    - 分組所有子項全滅後，分組整體消失。
 * 3. `getMenuForWorkbench`：入口函數，組合 MENU_TREES + filterMenuTree。
 *
 * 能力標注依據（admin 型樹，逐項）：
 * | 菜單項                                      | requiredCapability         |
 * |---------------------------------------------|---------------------------|
 * | /dashboard、/bulletin                        | （恆亮）                   |
 * | /bulletin/admin                              | bulletin.admin             |
 * | /certified-orgs                             | directory.read             |
 * | /settings/clients                           | client.manage              |
 * | /dispatch、/audit-tasks                     | case.read.all              |
 * | /audit-reports/pending-review               | audit-report.review        |
 * | /organic-materials                          | organic-material-application.manage |
 * | /risk-assessment                            | （恆亮，admin 型樹）        |
 * | /decisions                                  | decision.arrange           |
 * | /certificates                               | certificate.issue          |
 * | /tc                                         | tc.manage                  |
 * | /analytics 全組                             | analytics.read             |
 * | /settings/staff                             | user.manage                |
 * | 客戶字典×5 + 員工字典×3 + /settings/labs    | system-settings.write      |
 * | /settings/crops、/settings/standards        | standards.manage           |
 * | /settings/form-templates                    | form-template.manage       |
 * | /settings/document-library                  | system-settings.read       |
 * | /settings/numbering                         | numbering-rule.write       |
 * | /settings/conflict-of-interest              | conflict-of-interest.manage|
 * | /settings/notification-templates            | notification-template.manage|
 * | /settings/audit-log                         | audit-log.read             |
 * | /settings/recycle-bin                       | recycle-bin.read           |
 * | /audit/notices                              | audit-notice.manage        |
 * | /settings/roles                             | role.manage                |
 */

import type { MenuProps } from 'antd';
import { userCan, type Capability, type WorkbenchType } from '@zhcb/shared';

/** antd Menu 項目類型（與 layout-menu-config 共用） */
export type MenuItem = Required<MenuProps>['items'][number];

/**
 * 菜單項定義（靜態元數據，不帶 icon / React Node）。
 * icon 由各 Layout 組件透過 key 映射注入，菜單層不耦合 React。
 */
export interface MenuItemDef {
  /** 路由路徑（葉節點，以 `/` 開頭）或分組 key（非路徑形式） */
  key: string;
  /** 顯示標籤 */
  label: string;
  /**
   * 所需能力（可選）。
   * 未填 → 恆亮；填入 → 調用 `userCan` 判斷是否顯示。
   */
  requiredCapability?: Capability;
  /** 子項（分組節點） */
  children?: MenuItemDef[];
}

/* ═══════════════════════════════════════════════
 *  Admin 型基準樹（admin + manager 共用）
 *  差異由能力過濾產生：manager 無 user.manage / system-settings.write /
 *  numbering-rule.write，對應子項自動消失；
 *  audit-log / recycle-bin 由 Task 9 種子黑名單（hiddenKeys）處理。
 * ═══════════════════════════════════════════════ */
const ADMIN_TREE: MenuItemDef[] = [
  /* ─── 0. 工作台（直鏈，恆亮） ─── */
  { key: '/dashboard', label: '工作台' },

  /* ─── 1. 公佈欄（子菜單） ─── */
  // 查閱 /bulletin 恆亮；管理 /bulletin/admin 需 bulletin.admin 能力。
  {
    key: 'bulletin-group',
    label: '公佈欄',
    children: [
      { key: '/bulletin', label: '公告列表' },
      { key: '/bulletin/admin', label: '公告管理', requiredCapability: 'bulletin.admin' },
    ],
  },

  /* ─── 2. 獲證組織名錄（直鏈；序126 曾改名「組織名錄」，2026-09-12 回報序11 改回「獲證組織名錄」） ─── */
  { key: '/certified-orgs', label: '獲證組織名錄', requiredCapability: 'directory.read' },

  /* ─── 3. 稽核專區（子菜單，業務主入口） ─── */
  // 客戶建檔 → 派案 → 任務執行 → 待評審報告。
  // /settings/clients 需 client.manage；其餘稽核入口需 case.read.all。
  {
    key: 'audit-zone',
    label: '稽核專區',
    children: [
      // 2026-09-12 回報序12：「組織客戶檔案」改名為「客戶管理」
      { key: '/settings/clients', label: '客戶管理', requiredCapability: 'client.manage' },
      { key: '/dispatch', label: '派案專區', requiredCapability: 'case.read.all' },
      { key: '/audit-tasks', label: '稽/審核流程管理', requiredCapability: 'case.read.all' },
      // 序17：待評審報告一站式入口（audit-report.review = admin/manager）
      {
        key: '/audit-reports/pending-review',
        label: '待評審報告',
        requiredCapability: 'audit-report.review',
      },
    ],
  },

  /* ─── 4. 有機資材審查 + 風險評估（直鏈） ─── */
  // 有機資材需 organic-material-application.manage；風險評估在 admin 型樹中恆亮。
  {
    key: '/organic-materials',
    label: '有機資材審查',
    requiredCapability: 'organic-material-application.manage',
  },
  { key: '/risk-assessment', label: '風險評估' },

  /* ─── 5. 驗證決定管理（直鏈） ─── */
  { key: '/decisions', label: '驗證決定管理', requiredCapability: 'decision.arrange' },

  /* ─── 6. 證書管理（直鏈） ─── */
  { key: '/certificates', label: '證書管理', requiredCapability: 'certificate.issue' },

  /* ─── 7. 交易證明文件 (TC)（直鏈） ─── */
  { key: '/tc', label: '交易證明 (TC)', requiredCapability: 'tc.manage' },

  /* ─── 8. 統計分析（6 子項，全組需 analytics.read） ─── */
  {
    key: 'analytics-group',
    label: '統計分析',
    children: [
      { key: '/analytics', label: '統計總覽', requiredCapability: 'analytics.read' },
      { key: '/analytics/cases', label: '案件分析', requiredCapability: 'analytics.read' },
      { key: '/analytics/auditors', label: '稽核員排行', requiredCapability: 'analytics.read' },
      { key: '/analytics/certificates', label: '發證分析', requiredCapability: 'analytics.read' },
      { key: '/analytics/clients', label: '客戶分析', requiredCapability: 'analytics.read' },
      { key: '/analytics/financial', label: '財務分析', requiredCapability: 'analytics.read' },
    ],
  },

  /* ─── 9. 系統設置（19 子項，各自能力標注） ─── */
  {
    key: 'system-settings',
    label: '系統設置',
    children: [
      // 員工帳號管理（僅 admin = user.manage）
      { key: '/settings/staff', label: '員工帳號', requiredCapability: 'user.manage' },
      // 角色權限管理（動態 RBAC 配置界面，僅 admin = role.manage）
      { key: '/settings/roles', label: '角色權限管理', requiredCapability: 'role.manage' },
      // 客戶字典 ×5（僅 admin = system-settings.write）
      {
        key: '/settings/verification-schemes',
        label: '客戶字典 — 驗證方案',
        requiredCapability: 'system-settings.write',
      },
      {
        key: '/settings/verification-qualifications',
        label: '客戶字典 — 驗證資格',
        requiredCapability: 'system-settings.write',
      },
      {
        key: '/settings/verification-types',
        label: '客戶字典 — 驗證類型',
        requiredCapability: 'system-settings.write',
      },
      {
        key: '/settings/verification-categories',
        label: '客戶字典 — 驗證類別',
        requiredCapability: 'system-settings.write',
      },
      {
        key: '/settings/contract-sources',
        label: '客戶字典 — 合約來源',
        requiredCapability: 'system-settings.write',
      },
      // 員工字典 ×3（僅 admin = system-settings.write）
      {
        key: '/settings/departments',
        label: '員工字典 — 服務部門',
        requiredCapability: 'system-settings.write',
      },
      {
        key: '/settings/job-titles',
        label: '員工字典 — 職稱',
        requiredCapability: 'system-settings.write',
      },
      {
        key: '/settings/job-functions',
        label: '員工字典 — 聘用職務',
        requiredCapability: 'system-settings.write',
      },
      // 業務字典：實驗室（僅 admin = system-settings.write）
      {
        key: '/settings/laboratories',
        label: '業務字典 — 實驗室管理',
        requiredCapability: 'system-settings.write',
      },
      // 業務字典：作物（admin + manager = standards.manage）
      {
        key: '/settings/crops',
        label: '業務字典 — 作物管理',
        requiredCapability: 'standards.manage',
      },
      // 業務字典：有機資材查詢連結（序4：審查頁外部查詢連結可維護；admin + manager = standards.manage）
      {
        key: '/settings/organic-material-links',
        label: '業務字典 — 有機資材查詢連結',
        requiredCapability: 'standards.manage',
      },
      // 資材庫（有機/產銷履歷資材資料庫，序180 第6/7類；頁面既有但先前漏掛選單）
      {
        key: '/settings/materials',
        label: '資材庫管理',
        requiredCapability: 'materials.read',
      },
      // 表單管理（admin + manager = form-template.manage；2026-09-25 序34 更名）
      {
        key: '/settings/form-templates',
        label: '表單管理',
        requiredCapability: 'form-template.manage',
      },
      // 文件資料庫（序180/181：管理手冊/公文/法規標準/稽核報告彙整，admin+manager = system-settings.read）
      {
        key: '/settings/document-library',
        label: '文件資料庫',
        requiredCapability: 'system-settings.read',
      },
      // 編號規則（僅 admin = numbering-rule.write）
      {
        key: '/settings/numbering',
        label: '編號規則',
        requiredCapability: 'numbering-rule.write',
      },
      // 法規/標準/資材庫（admin + manager = standards.manage）
      {
        key: '/settings/standards',
        label: '法規/標準/資材庫',
        requiredCapability: 'standards.manage',
      },
      // 利益迴避（admin + manager = conflict-of-interest.manage）
      {
        key: '/settings/conflict-of-interest',
        label: '利益迴避設定',
        requiredCapability: 'conflict-of-interest.manage',
      },
      // 通知模板（admin + manager = notification-template.manage）
      {
        key: '/settings/notification-templates',
        label: '通知模板',
        requiredCapability: 'notification-template.manage',
      },
      // 操作日誌（admin + manager = audit-log.read；Task 9 種子黑名單額外對 manager 隱藏）
      { key: '/settings/audit-log', label: '操作日誌/審計', requiredCapability: 'audit-log.read' },
      // 資料恢復（admin + manager = recycle-bin.read；Task 9 種子黑名單額外對 manager 隱藏）
      {
        key: '/settings/recycle-bin',
        label: '資料恢復/回收站',
        requiredCapability: 'recycle-bin.read',
      },
      // 全部稽核通知（跨任務彙總；admin + manager = audit-notice.manage）
      {
        key: '/audit/notices',
        label: '全部稽核通知',
        requiredCapability: 'audit-notice.manage',
      },
    ],
  },
];

/* ═══════════════════════════════════════════════
 *  Accountant 基準樹
 *  會計（序52）：聚焦報價／繳費，菜單極簡。
 *  全恆亮（能力校驗在 API 層，菜單僅按角色分流）。
 * ═══════════════════════════════════════════════ */
const ACCOUNTANT_TREE: MenuItemDef[] = [
  { key: '/dashboard', label: '工作台' },
  { key: '/bulletin', label: '公佈欄' },
  { key: '/dispatch', label: '派案專區' },
  { key: '/me/notifications', label: '通知' },
];

/* ═══════════════════════════════════════════════
 *  Decider 基準樹
 *  驗證決定委員，以驗證決定為核心，全恆亮。
 * ═══════════════════════════════════════════════ */
const DECIDER_TREE: MenuItemDef[] = [
  { key: '/dashboard', label: '工作台' },
  { key: '/bulletin', label: '公佈欄' },
  {
    key: 'audit-zone',
    label: '稽核資料查閱',
    children: [
      { key: '/audit/orgs', label: '組織資訊' },
      { key: '/risk-assessment', label: '風險評估' },
    ],
  },
  { key: '/decisions', label: '驗證決定管理' },
  { key: '/certificates', label: '證書管理' },
];

/* ═══════════════════════════════════════════════
 *  Auditor 基準樹
 *  聚焦稽核執行，/decisions 僅具備 decision.vote 能力時可見。
 *  /audit-tasks 需 case.read.own（auditor 具備；Task 8 改為能力標注，取代原 roles 參數）。
 * ═══════════════════════════════════════════════ */
const AUDITOR_TREE: MenuItemDef[] = [
  { key: '/dashboard', label: '工作台' },
  { key: '/bulletin', label: '公佈欄' },
  // 稽核任務（稽核員主要作業入口，需 case.read.own）
  { key: '/audit-tasks', label: '稽/審核流程管理', requiredCapability: 'case.read.own' },
  // 有機資材審查（序77.2，稽核員可用）
  { key: '/organic-materials', label: '有機資材審查' },
  // 審議任務：稽核員兼任驗證決定委員時顯示（decision.vote），取代原 roles 參數邏輯
  { key: '/decisions', label: '審議任務', requiredCapability: 'decision.vote' },
  /**
   * 合約評審（2026-09-25 序47）：主角色為稽核員、兼任「驗證組組長」者（多角色）
   * 落在 auditor 工作台，原本看不到派案專區而無法進入案件的合約評審作業。
   * 具 case.contract_review.lead_submit（組長填寫/簽名提交）能力時顯示，
   * 直達派案專區列表 → 案件詳情「合約評審」步驟進行填寫與簽名。
   */
  {
    key: '/dispatch',
    label: '合約評審',
    requiredCapability: 'case.contract_review.lead_submit',
  },
  // 問題與報告
  {
    key: 'issues-reports',
    label: '問題與報告',
    children: [
      { key: '/non-conformities/list', label: '問題追蹤' },
      { key: '/me/report-styles', label: '報告樣式模板' },
    ],
  },
  // 個人中心
  {
    key: 'personal',
    label: '個人中心',
    children: [
      { key: '/auditor/schedule', label: '稽核行程' },
      { key: '/me/notifications', label: '通知' },
      // 序44：個人設定 = 本人資料/修改密碼頁（原誤連員工帳號管理 /settings/staff）
      { key: '/me/profile', label: '個人設定' },
    ],
  },
];

/**
 * 各工作台基準菜單樹。
 * admin 與 manager 共用 ADMIN_TREE，manager 差異由能力過濾自動產生。
 */
export const MENU_TREES: Record<WorkbenchType, MenuItemDef[]> = {
  admin: ADMIN_TREE,
  manager: ADMIN_TREE, // 共用 admin 型樹，差異由 filterMenuTree 能力過濾產生
  accountant: ACCOUNTANT_TREE,
  decider: DECIDER_TREE,
  auditor: AUDITOR_TREE,
};

/**
 * 遞歸過濾菜單樹，返回符合能力且未被隱藏的 antd MenuItem[]。
 *
 * 過濾規則：
 * 1. 命中 hiddenKeys（分組 key 或葉 key）→ 強制隱藏，不論能力。
 * 2. 有 requiredCapability → 調用 `userCan(user, cap)` 判斷；無能力 → 隱藏。
 * 3. 無 requiredCapability → 恆亮（仍受 hiddenKeys 限制）。
 * 4. 分組節點（有 children）：過濾子項後若子項為空 → 整組隱藏。
 *
 * @param tree         菜單定義樹（MenuItemDef[]）
 * @param user         當前用戶（含可選 capabilities/roles，傳給 userCan）
 * @param hiddenKeys   強制隱藏的 key 集合（Task 9 種子黑名單或頁面級動態隱藏）
 * @returns            antd MenuItem[]（可直接傳給 antd Menu items prop）
 */
export function filterMenuTree(
  tree: MenuItemDef[],
  user: { capabilities?: readonly string[]; roles?: readonly string[] },
  hiddenKeys: readonly string[] = [],
): MenuItem[] {
  const hiddenSet = new Set(hiddenKeys);
  const result: MenuItem[] = [];

  for (const def of tree) {
    // 1. hiddenKeys 命中 → 跳過（分組與葉均有效）
    if (hiddenSet.has(def.key)) continue;

    // 2. 能力校驗（無 requiredCapability 恆通過）
    if (def.requiredCapability && !userCan(user, def.requiredCapability)) continue;

    // 3. 分組節點（有 children）
    if (def.children && def.children.length > 0) {
      const filteredChildren = filterMenuTree(def.children, user, hiddenKeys);
      // 子項全滅 → 整組隱藏
      if (filteredChildren.length === 0) continue;
      result.push({
        key: def.key,
        label: def.label,
        children: filteredChildren,
      });
      continue;
    }

    // 4. 葉節點
    result.push({ key: def.key, label: def.label });
  }

  return result;
}

/**
 * 取得指定工作台類型的菜單（能力過濾後的 antd MenuItem[]）。
 *
 * @param type       工作台類型（WorkbenchType）
 * @param user       當前用戶（含可選 capabilities/roles）
 * @param hiddenKeys 強制隱藏的 key 集合（預設空）
 * @returns          antd MenuItem[]
 */
export function getMenuForWorkbench(
  type: WorkbenchType,
  user: { capabilities?: readonly string[]; roles?: readonly string[] },
  hiddenKeys: readonly string[] = [],
): MenuItem[] {
  const tree = MENU_TREES[type];
  return filterMenuTree(tree, user, hiddenKeys);
}
